import { action, computed, makeObservable, observable } from 'mobx'

import {
  CalendricalType,
  ILocationPermitField,
  IPermitTypeField,
  IPreciseLocation,
  ISiteLocation,
  IWorkflowStep,
  LocationType,
  PermitFieldType,
  SitePermitStatus,
  WorkflowDurationType,
  WorkflowStepType,
} from '~/client/graph/types.generated'
import ProjectDateStore, {
  DAYS_IN_MONTH,
  DAYS_IN_WEEK,
  isAfter,
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
} from '~/client/src/shared//stores/ui/ProjectDate.store'
import { globeVisibleItems } from '~/client/src/shared/components/SitemapHelpers/models/buildGlobeViewItems'
import IPermitFieldDto from '~/client/src/shared/models/IPermitFieldDto'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import PermitField from '~/client/src/shared/models/PermitField'
import RecurringWorkflowSettings from '~/client/src/shared/models/RecurringWorkflowSettings'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import RecurringWorkflowSettingsStore from '~/client/src/shared/stores/domain/RecurringWorkflowSettings.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { isLocationOfferedForField } from '~/client/src/shared/utils/isLocationOfferedForField'
import {
  globeLbsTag,
  resolvePinSnapLocationId,
} from '~/client/src/shared/utils/pinLocationSnap'
import { isRestrictedByPermittedCompanies } from '~/client/src/shared/utils/resourceRestrictionHelpers'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'
import {
  areArraysEqual,
  areArraysEqualUnordered,
  copyObject,
} from '~/client/src/shared/utils/util'

import { getWorkflowStepLevel } from '../../../constants/formStatusesTags'
import Localization from '../../../localization/LocalizationManager'
import LocationBase from '../../../models/LocationObjects/LocationBase'
import SitemapItemsStore from '../../domain/SitemapItems.store'
import PermitFieldsBaseStore, {
  IPermitFormField,
} from './PermitFieldsBase.store'

export enum SpecificPermitFieldTypes {
  Type = 'type',
  Title = 'title',
  SpecificInspectionOptions = 'spectific-inspection-options',
  RecurringOptions = 'recurring-options',
}

interface IFormTypeMaxDuration {
  maxDuration: number
  maxDurationType: WorkflowDurationType
  isEnabled: boolean
}

const getInitFormStatus = (
  formType: WorkflowType,
  isLastStep?: boolean,
): SitePermitStatus => {
  if (isLastStep || formType?.workflowSteps?.length === 1) {
    return SitePermitStatus.Done
  }
  if (formType?.initialStep?.type === WorkflowStepType.Submission) {
    return SitePermitStatus.Submitted
  }
  return SitePermitStatus.Requested
}

// The snap field is the initial step's top-level Location field: it has no parent
// instance, so the picker computes no LBS restriction for it either.
const NO_LBS_REFERENCE = new Set<string>()

interface IPinSeed {
  entity: Workflow
  location: ISiteLocation
  pin: IPreciseLocation
}

const defaultPrePopulatedUserTypes = [
  PermitFieldType.Requester,
  PermitFieldType.Assignee,
]

// Title/Type read the translator inside their getters — no language at module-eval
// time. These two have no keys yet.
const _inspectionOptionsCaption = 'Inspection Options'
const _recurringOptionsCaption = 'Recurring Options'

export default class PermitWorkflowFieldsStore
  extends PermitFieldsBaseStore
  implements IPermitFieldsStore
{
  @observable public existingEntity: Workflow = null
  @observable public editableEntity: Workflow = null

  @observable public recurringSettings: RecurringWorkflowSettings = null
  @observable public previewTemplate: WorkflowType = null

  private pinSeed: IPinSeed = null
  // The primary value the snap last stored, held by identity: any other write replaces the object.
  private pinLocation: ISiteLocation = null
  private readonly declinedPinLocationIds = new WeakMap<Workflow, Set<string>>()

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly companiesStore: CompaniesStore,
    private readonly recurringSettingsStore: RecurringWorkflowSettingsStore,
    locationStore: LocationAttributesStore,
    private readonly sitemapItemsStore: SitemapItemsStore,
    private readonly closuresStore?: ClosuresStore,
  ) {
    super(eventsStore.appState, locationStore)

    makeObservable(this)
  }

  @action.bound
  public setPreviewTemplate(template: WorkflowType) {
    this.previewTemplate = template
  }

  public get template(): WorkflowType {
    return (
      this.previewTemplate ||
      this.workflowTypesStore.getPermitTypeById(this.editableEntity?.typeId)
    )
  }

  public get permitTypeField(): IPermitFormField {
    const { typeId } = this.editableEntity
    return {
      id: SpecificPermitFieldTypes.Type,
      type: SpecificPermitFieldTypes.Type,
      caption: Localization.translator.type,
      isChanged: !!this.existingEntity && typeId !== this.existingEntity.typeId,
      isRequired: true,
      isValid: !!typeId,
    }
  }

  public get titleField(): IPermitFormField {
    const { title } = this.editableEntity
    const { isWorkflowTitleMandatory } = this.template || {}
    const { isAdminOrFormsMaster } =
      this.eventsStore.appState.userActiveProjectSettings

    return {
      id: SpecificPermitFieldTypes.Title,
      type: SpecificPermitFieldTypes.Title,
      caption: Localization.translator.title,
      isChanged: !!this.existingEntity && title !== this.existingEntity?.title,
      isRequired: isWorkflowTitleMandatory,
      isValid:
        (isAdminOrFormsMaster && !title) ||
        !!title?.trim() ||
        !isWorkflowTitleMandatory,
    }
  }

  public get inspectionOptionsField(): IPermitFormField {
    const { specificInspectionOptions } = this.editableEntity

    const areInspectionOptionsChanged =
      specificInspectionOptions?.deadlineTime !==
        this.existingEntity?.specificInspectionOptions?.deadlineTime ||
      specificInspectionOptions?.inspectionFrequency !==
        this.existingEntity?.specificInspectionOptions?.inspectionFrequency ||
      specificInspectionOptions?.inspectionFrequencyType !==
        this.existingEntity?.specificInspectionOptions
          ?.inspectionFrequencyType ||
      !areArraysEqual(
        specificInspectionOptions?.selectedDaysToRepeat || [],
        this.existingEntity?.specificInspectionOptions?.selectedDaysToRepeat ||
          [],
      )

    return {
      id: SpecificPermitFieldTypes.SpecificInspectionOptions,
      type: SpecificPermitFieldTypes.SpecificInspectionOptions,
      caption: _inspectionOptionsCaption,
      isChanged: areInspectionOptionsChanged,
      isValid:
        !specificInspectionOptions ||
        (!!specificInspectionOptions?.deadlineTime &&
          !!specificInspectionOptions?.inspectionFrequency &&
          !!specificInspectionOptions?.inspectionFrequencyType),
    }
  }

  public get recurringOptionsField(): IPermitFormField {
    const editedSettings = this.recurringSettings
    return {
      id: SpecificPermitFieldTypes.RecurringOptions,
      type: SpecificPermitFieldTypes.RecurringOptions,
      caption: _recurringOptionsCaption,
      isChanged: this.isRecurringSettingsChanged,
      isRequired: false,
      isValid:
        !editedSettings ||
        !!(
          editedSettings.frequency &&
          editedSettings.frequencyType &&
          this.isRecurringSettingsDateValid &&
          (editedSettings.frequencyType === CalendricalType.Week
            ? editedSettings.selectedDays?.length
            : true)
        ),
    }
  }

  public get isRecurringSettingsDateValid(): boolean {
    if (
      !this.recurringSettings ||
      (this.editableEntity?.id && !this.isRecurringSettingsChanged)
    ) {
      return true
    }

    if (!this.recurringSettings.endDate) {
      return false
    }

    const { endOfDay, startOfDay, addDays } = this.projectDateStore
    const startDate = startOfDay(this.editableEntity.startDate || new Date())
    const maxDate = endOfDay(
      addDays(startDate, this.template.recurringOptions?.maxDuration || 0),
    )
    const endDate = endOfDay(this.recurringSettings.endDate)

    return endDate <= maxDate && endDate >= startDate
  }

  public get isRecurringSettingsChanged(): boolean {
    const existingSettings = this.existingRecurringSettings
    const editedSettings = this.recurringSettings

    if (!editedSettings) {
      return false
    }

    return (
      !!this.existingEntity &&
      (existingSettings?.frequency !== editedSettings?.frequency ||
        existingSettings?.frequencyType !== editedSettings?.frequencyType ||
        !areArraysEqualUnordered(
          existingSettings?.selectedDays || [],
          editedSettings?.selectedDays || [],
        ) ||
        existingSettings?.endDate !== editedSettings?.endDate)
    )
  }

  public get datesField(): IPermitFormField {
    const { startDate, endDate } = this.editableEntity
    const { isAdminOrFormsMaster } =
      this.eventsStore.appState.userActiveProjectSettings
    const { shouldBlockOnNonWorkTimes } = this.template
    const { isIntervalInsideWorkingHours } = this.projectDateStore

    const field = this.workflowSteps[0].fields?.find(
      f => f.type === PermitFieldType.StartFinishDates,
    )
    const areDatesMandatory = field?.isMandatory

    const areDatesChanged =
      !!this.existingEntity &&
      (startDate !== this.existingEntity?.startDate ||
        endDate !== this.existingEntity?.endDate)

    const hasStartAndEnd = Boolean(startDate && endDate)
    // The whole interval, not just its ends: a start and finish can both be
    // inside working hours while a holiday, an unworked day or a closed stretch
    // sits between them. The weekday is still never asked — worked spans decide,
    // so hours an overnight window reaches into still count.
    const isInWorkTime =
      hasStartAndEnd && isIntervalInsideWorkingHours(startDate, endDate)

    return {
      id: field.id,
      type: field.type,
      caption: field.caption,
      isChanged: areDatesChanged,
      isRequired: areDatesMandatory,
      isValid: hasStartAndEnd && this.areDatesWithinMaxDuration && isInWorkTime,
      isValidIgnored:
        isAdminOrFormsMaster ||
        (this.areDatesWithinMaxDuration && !shouldBlockOnNonWorkTimes),
    }
  }

  protected get canIgnoreValidation(): boolean {
    return true
  }

  public get areDatesWithinMaxDuration(): boolean {
    const { startDate, endDate } = this.editableEntity
    const { validateMaxDuration } = this.projectDateStore
    const { maxDuration, maxDurationType, isEnabled } = this.formTypeMaxDuration

    if (!isEnabled) {
      return true
    }

    return validateMaxDuration(startDate, endDate, maxDurationType, maxDuration)
  }

  public get maxDurationMessage(): string {
    const label = this.maxDurationLabel
    return label && Localization.translator.maxDurationError(label)
  }

  // The longest a workflow of this type may run: "All day", "Work week", "8 hours".
  public get maxDurationLabel(): string {
    const { maxDuration, maxDurationType, isEnabled } = this.formTypeMaxDuration
    const { allDay, workWeek, xHours, xMinutes, xDays, xWeeks, xMonths } =
      Localization.translator

    if (!isEnabled) {
      return null
    }

    if (maxDurationType === WorkflowDurationType.AllDay) {
      return allDay
    }

    if (maxDurationType === WorkflowDurationType.WorkWeek) {
      return workWeek
    }

    let maxDurationString: string = xMinutes(maxDuration)

    // If duration is bigger than 1 day, convert to days first and round to integer
    if (maxDuration >= MINUTES_IN_DAY) {
      const totalDays = Math.round(maxDuration / MINUTES_IN_DAY)

      if (totalDays >= DAYS_IN_MONTH) {
        const months = Math.round(totalDays / DAYS_IN_MONTH)
        maxDurationString = `${months} ${xMonths(months)}`
      } else if (totalDays >= DAYS_IN_WEEK) {
        if (totalDays % DAYS_IN_WEEK === 0) {
          const weeks = totalDays / DAYS_IN_WEEK
          maxDurationString = `${weeks} ${xWeeks(weeks)}`
        } else {
          maxDurationString = `${totalDays} ${xDays(totalDays)}`
        }
      } else {
        maxDurationString = `${totalDays} ${xDays(totalDays)}`
      }
    } else if (maxDuration >= MINUTES_IN_HOUR) {
      const hours = Math.round(maxDuration / MINUTES_IN_HOUR)
      maxDurationString = xHours(hours)
    }

    return maxDurationString
  }

  public get formTypeMaxDuration(): IFormTypeMaxDuration {
    const { typeId } = this.editableEntity
    if (!typeId) {
      return null
    }
    const formType = this.workflowTypesStore.getPermitTypeById(typeId)
    const {
      maxDurationInMinutes,
      maxDurationOptions: { durationType, isEnabled },
    } = formType
    return {
      maxDuration: maxDurationInMinutes,
      maxDurationType: durationType,
      isEnabled,
    }
  }

  @computed
  public get currentStepFieldIds(): Set<string> {
    const { currentWorkflowStepId } = this.existingEntity || this.editableEntity
    const currentStep = this.workflowSteps.find(
      s => s.id === currentWorkflowStepId,
    )
    const currentStepFieldIds = currentStep?.fields?.map(({ id }) => id) || []
    const currentStepCondFieldIds =
      currentStep?.conditionalFields?.flatMap(({ values }) =>
        values.map(v => v.id),
      ) || []

    return new Set(currentStepFieldIds.concat(currentStepCondFieldIds))
  }

  @computed
  protected get fieldIdsToIgnore(): string[] {
    return Array.from(this.currentStepFieldIds)
  }

  @computed
  public get previousStepsFields(): IPermitFormField[] {
    return this.fields.filter(f => !this.fieldIdsToIgnore.includes(f.id))
  }

  @computed
  public get firstTwoStepsFieldIds(): Set<string> {
    const [first, second] = this.template.stepsWithoutRecurring

    return new Set([
      this.permitTypeField.id,
      this.titleField.id,
      this.inspectionOptionsField.id,
      this.recurringOptionsField.id,
      ...(first?.fields.map(({ id }) => id) || []),
      ...(first?.conditionalFields?.flatMap(({ values }) =>
        values.map(({ id }) => id),
      ) || []),
      ...(second?.fields.map(({ id }) => id) || []),
      ...(second?.conditionalFields?.flatMap(({ values }) =>
        values.map(({ id }) => id),
      ) || []),
    ])
  }

  @computed
  public get fields(): IPermitFormField[] {
    const permitFields = this.workflowSteps
      .flatMap(s => s.fields)
      ?.reduce((fieldsList, typeField) => {
        if (!typeField.isShown) {
          return fieldsList
        }

        if (typeField.type === PermitFieldType.StartFinishDates) {
          fieldsList.push(this.datesField)
          return fieldsList
        }

        fieldsList.push(this.getFormFieldModel(typeField))

        if (typeField.type === PermitFieldType.Table) {
          fieldsList.push(...(this.getTableFieldModels(typeField) || []))
        }

        fieldsList.push(...this.getConditionalFormFields(typeField))

        return fieldsList
      }, [] as IPermitFormField[])

    return [
      this.permitTypeField,
      this.titleField,
      this.inspectionOptionsField,
      this.recurringOptionsField,
      ...(permitFields || []),
    ]
  }

  @computed
  protected get workflowSteps(): IWorkflowStep[] {
    if (!this.template) {
      return []
    }

    const { stepsWithoutRecurring, isInspectionType } = this.template
    const { currentWorkflowStepId, isActive } =
      this.existingEntity || this.editableEntity

    const currentStepIndex = stepsWithoutRecurring.findIndex(
      s => s.id === currentWorkflowStepId,
    )

    if (currentStepIndex === -1) {
      return stepsWithoutRecurring.slice(0, 1)
    }

    if (
      isInspectionType &&
      !this.isLastInspectionDay &&
      isActive(this.template)
    ) {
      return stepsWithoutRecurring.slice(0, currentStepIndex)
    }

    return stepsWithoutRecurring.slice(0, currentStepIndex + 1)
  }

  public get enabledPermitTypes(): WorkflowType[] {
    return this.workflowTypesStore.enabledTypes
  }

  public get actualPermiTypes(): WorkflowType[] {
    return this.workflowTypesStore.actualTypes
  }

  public get isLastInspectionDay(): boolean {
    const { isSameDay } = this.projectDateStore
    const { endDate } = this.editableEntity
    const now = Date.now()

    return (
      this.template?.isInspectionType &&
      (isSameDay(endDate, now) || isAfter(now, endDate))
    )
  }

  @computed
  public get editableRecurringSettings(): RecurringWorkflowSettings {
    return this.recurringSettings || this.existingRecurringSettings
  }

  @computed
  public get existingRecurringSettings(): RecurringWorkflowSettings {
    if (!this.existingEntity?.recurringSettingsId) {
      return null
    }

    return (
      this.recurringSettingsStore.getById(
        this.existingEntity.recurringSettingsId,
      ) || null
    )
  }

  @action.bound
  public setRecurringSettings(settings: RecurringWorkflowSettings) {
    this.recurringSettings = settings
  }

  @action.bound
  public setDefaultSitePermit(
    permitToEdit?: Workflow,
    typeId?: string,
    isLastStep?: boolean,
    prepopulatedFields?: IPermitFieldDto[],
    preSelectedSiteLocation?: ISiteLocation,
  ) {
    if (permitToEdit) {
      this.editableEntity = permitToEdit.copy()
      this.existingEntity = permitToEdit
      this.loadUserSubmissionFields(permitToEdit)
      return
    }

    const { addMinutes, getAllDayWindowForDate, getLastWorkingDay } =
      this.projectDateStore
    const {
      user,
      activeProject,
      preSelectedPermitTypeId,
      userActiveProjectSettings,
    } = this.eventsStore.appState
    const { id: projectId } = activeProject
    // The seeded day is today, and what today works is what it should open on —
    // the project-wide window mirrors the schedule's most common range, which may
    // match no single day. A day reached only by last night's shift gets that
    // shift's remainder (STX1-983).
    const { startTime, endTime } = getAllDayWindowForDate(new Date())

    const initPermitType =
      this.actualPermiTypes.find(({ id }) => typeId === id) ||
      this.actualPermiTypes.find(({ id }) => preSelectedPermitTypeId === id) ||
      this.enabledPermitTypes[0]

    let endDate = null

    switch (initPermitType.defaultDurationOptions.durationType) {
      case WorkflowDurationType.AllDay:
        endDate = endTime
        break
      case WorkflowDurationType.WorkWeek:
        endDate = getLastWorkingDay(startTime)
        break
      default:
        endDate = addMinutes(startTime, initPermitType.defaultDurationInMinutes)
        break
    }

    const company = this.companiesStore.getCompanyById(
      userActiveProjectSettings?.companyId,
      true,
    )

    const initialPermitFields: IPermitFieldDto[] = prepopulatedFields || []

    const locationField = initPermitType?.locationField

    const preSelectedLocation = copyObject(
      preSelectedSiteLocation ??
        this.eventsStore.appState.preSelectedSiteLocation,
    ) as ISiteLocation

    const seededPin =
      this.sitemapItemsStore &&
      locationField &&
      preSelectedLocation?.preciseLocation &&
      !preSelectedLocation.id
        ? preSelectedLocation.preciseLocation
        : null
    if (seededPin && !locationField.allowPreciseLocation) {
      preSelectedLocation.preciseLocation = null
    }

    if (initPermitType && locationField) {
      initialPermitFields.push({
        instanceId: BsonObjectId.generate(),
        fieldId: locationField.id,
        type: PermitFieldType.Location,
        locationValues:
          preSelectedLocation?.id && this.sitemapItemsStore
            ? [preSelectedLocation]
            : [],
      } as ILocationPermitField)
    }
    const typeInitialFields = initPermitType?.initialStep?.fields || []
    const initialStepId = isLastStep
      ? initPermitType?.lastStep?.id
      : initPermitType?.initialStep?.id

    if (initPermitType?.id && company) {
      initialPermitFields.push(
        ...typeInitialFields
          .filter(({ type }) => type === PermitFieldType.Company)
          .map(f => ({
            instanceId: BsonObjectId.generate(),
            fieldId: f.id,
            type: f.type,
            objectIdValues: [company.id],
          })),
      )
    }
    if (initPermitType?.id) {
      initialPermitFields.push(
        ...typeInitialFields
          .filter(({ type }) => defaultPrePopulatedUserTypes.includes(type))
          .map(f => ({
            instanceId: BsonObjectId.generate(),
            fieldId: f.id,
            type: f.type,
            objectIdValues: [user.id],
          })),
      )
    }

    this.editableEntity = new Workflow(
      null,
      initPermitType?.id,
      getInitFormStatus(initPermitType, isLastStep),
      projectId,
      startTime.getTime(),
      endDate.getTime(),
      EMPTY_STRING,
      initPermitType.categoryId,
      initPermitType.type,
      [],
      [],
      initialStepId,
      initialStepId
        ? getWorkflowStepLevel(initialStepId, initPermitType.workflowSteps)
        : 0,
      initPermitType?.isAutoActivationEnabled,
      user.id,
      null,
      null,
      null,
      null,
      null,
      initialPermitFields,
      false,
      initPermitType.defaultDurationOptions.durationType ===
        WorkflowDurationType.AllDay,
    )
    this.pinSeed = seededPin
      ? {
          entity: this.editableEntity,
          location: preSelectedLocation,
          pin: seededPin,
        }
      : null
    this.snapSeededPin()
  }

  @action.bound
  public snapSeededPin() {
    const seed = this.pinSeed
    const field = this.template?.locationField
    if (!seed || seed.entity !== this.editableEntity || !field) return
    const current = this.getFieldValues<ISiteLocation>(field.id)?.[0]
    if (!this.isPinLocation(current)) return
    const closest = this.resolveClosestAllowedLocation(field, seed.pin)
    if (!closest || closest.id === current?.id) return
    this.writePinLocation(field, {
      ...(current ?? seed.location),
      id: closest.id,
      type: closest.type,
    })
  }

  /** Returns the map-view Location to ask about; only a user-owned Location gets one. */
  @action.bound
  public applyPinMove(pin: IPreciseLocation): LocationBase | null {
    const field = this.template?.locationField
    if (!field || !pin) return null
    const current = this.getFieldValues<ISiteLocation>(field.id)?.[0]
    const preciseLocation = {
      ...(current?.preciseLocation ?? {}),
      ...pin,
    } as IPreciseLocation
    if (this.isPinLocation(current)) {
      const closest = this.resolveClosestAllowedLocation(field, preciseLocation)
      this.writePinLocation(field, {
        ...(current ?? {}),
        // No allowed location to snap to is not evidence the pin left the one it had.
        ...(closest ? { id: closest.id, type: closest.type } : {}),
        preciseLocation,
      } as ISiteLocation)
      return null
    }
    this.changePrimaryLocationValue(field, { ...current, preciseLocation })
    return this.pinLocationOffer(field, preciseLocation, current)
  }

  public declinePinLocation(locationId: string) {
    const ids =
      this.declinedPinLocationIds.get(this.editableEntity) ?? new Set<string>()
    this.declinedPinLocationIds.set(this.editableEntity, ids.add(locationId))
  }

  private isPinLocation(value: ISiteLocation): boolean {
    return !value?.id || value === this.pinLocation
  }

  private writePinLocation(field: IPermitTypeField, next: ISiteLocation) {
    // A secondary the pin lands on was the user's pick: promoted, it stays theirs.
    const isPicked =
      field.isMultiple &&
      this.getFieldValues<ISiteLocation>(field.id)
        .filter(value => value?.id)
        .slice(1)
        .some(value => value.id === next.id)
    this.changePrimaryLocationValue(field, next)
    this.pinLocation = isPicked
      ? null
      : (this.getFieldValues<ISiteLocation>(field.id)[0] ?? null)
  }

  private pinLocationOffer(
    field: IPermitTypeField,
    pin: IPreciseLocation,
    current: ISiteLocation,
  ): LocationBase | null {
    const offer = this.resolveClosestAllowedLocation(field, pin, true)
    if (
      !offer ||
      offer.is(LocationType.ProjectLocation) ||
      this.declinedPinLocationIds.get(this.editableEntity)?.has(offer.id)
    ) {
      return null
    }
    const placed = this.locationStore.allItemsMapWithDeleted[current.id]
    const held = placed ? this.locationStore.getAncestryChain(placed) : []
    return offer.id === current.id || held.some(l => l.id === offer.id)
      ? null
      : offer
  }

  // Camera-independent on purpose: seed, pin moves and the form must agree.
  public resolveClosestAllowedLocation = (
    locationField: IPermitTypeField,
    pin: Pick<IPreciseLocation, 'latitude' | 'longitude' | 'globeViewId'>,
    isMapViewOnly = false,
  ): LocationBase | null => {
    if (!this.sitemapItemsStore || !this.editableEntity) return null
    // Read once: a computed read outside a reaction is rebuilt on every access
    const { byAssignedId } = this.sitemapItemsStore
    const {
      allLocationsWithoutEquipment,
      allLocationsWithoutEquipmentMap,
      hierarchyAllAttributesMap,
    } = this.locationStore
    const { isAdminOrFormsMaster } =
      this.eventsStore.appState.userActiveProjectSettings
    const { startDate, endDate, companyIds, mainFieldsByIdMap } =
      this.editableEntity
    const workflowType = this.template?.type
    const globe = this.eventsStore.appState.globeViews.get(pin.globeViewId)
    const isClosed = (id: string) =>
      !!startDate &&
      !!endDate &&
      !!this.closuresStore?.isClosedForWorkflowType(
        id,
        workflowType,
        new Date(startDate),
        new Date(endDate),
      )
    const id = resolvePinSnapLocationId(
      {
        globeItems: globeVisibleItems({
          globe,
          getSitemapItem: itemId => this.sitemapItemsStore.byId.get(itemId),
          getDataObject: assignedId => hierarchyAllAttributesMap[assignedId],
          projectCenter: this.eventsStore.appState.projectAddress?.center,
        }),
        lbsTagLocationId:
          globe && !isMapViewOnly
            ? globeLbsTag(this.locationStore.globeAssignedLocations(globe.id))
                .lbsTagLocationId
            : null,
        projectItems: isMapViewOnly
          ? []
          : allLocationsWithoutEquipment.map(l => ({
              id: l.id,
              // By id alone: a pin keeps the kind it was placed under, which a
              // re-kinded location no longer has (STX1-1187)
              coordinates: byAssignedId.get(l.id)?.coordinates ?? null,
            })),
        isAllowed: locationId => {
          const location = allLocationsWithoutEquipmentMap[locationId]
          return (
            !!location &&
            isLocationOfferedForField(
              locationField,
              location,
              NO_LBS_REFERENCE,
              mainFieldsByIdMap,
            ) &&
            (isAdminOrFormsMaster ||
              (!isRestrictedByPermittedCompanies(
                location.permittedCompanies,
                companyIds,
              ) &&
                !isClosed(locationId)))
          )
        },
      },
      { latitude: pin.latitude, longitude: pin.longitude },
    )
    return (id && allLocationsWithoutEquipmentMap[id]) || null
  }

  @action.bound
  public setDuplicateWorkflowData(workflow: Workflow) {
    if (!workflow) return

    const formType = this.workflowTypesStore.getPermitTypeById(workflow.typeId)

    this.editableEntity = workflow.getCopyForDuplicate(
      getInitFormStatus(formType),
      formType,
    )
  }

  public setInitialStatus = (isDraft: boolean = false) => {
    if (isDraft) {
      this.editableEntity.setStatus(SitePermitStatus.Draft)
      return
    }
    this.editableEntity.setStatus(getInitFormStatus(this.template))
  }

  @action.bound
  private loadUserSubmissionFields(permit: Workflow) {
    const { currentWorkflowStepId } = permit
    const userId = this.eventsStore.appState?.user?.id

    if (!currentWorkflowStepId || !userId) return

    const template = this.workflowTypesStore.getPermitTypeById(permit.typeId)
    if (!template) return

    const requireAllSubmissions = template.getStepRequireAllSubmissions(
      currentWorkflowStepId,
    )
    if (!requireAllSubmissions) return

    const userSubmission = permit.getUserSubmission(
      currentWorkflowStepId,
      userId,
    )
    if (!userSubmission?.fields?.length) return

    const submissionFieldIds = new Set(
      userSubmission.fields.map(f => f.fieldId),
    )

    this.editableEntity.fields = this.editableEntity.fields.filter(
      f => !submissionFieldIds.has(f.fieldId),
    )

    userSubmission.fields.forEach(submissionField => {
      if (submissionField instanceof PermitField) {
        const clone = new PermitField(
          submissionField.instanceId,
          submissionField.fieldId,
          submissionField.type,
          submissionField.parentInstanceId,
          submissionField.parentValueIndex,
        )
        clone.values = [...submissionField.values]
        this.editableEntity.fields.push(clone)
      } else {
        this.editableEntity.fields.push(
          PermitField.fromDto(submissionField as IPermitFieldDto),
        )
      }
    })
  }
}
